import React, { useEffect, useMemo, useRef, useState } from 'react';
import StepShell from '../StepShell';
import EyeIcon from '../EyeIcon';
import { apiHandler } from '../../../../../api/apihandler';
import { PROFILE_UPDATE, PROFILE_PIC_UPDATE, PROFILE_COVER_PIC_UPDATE } from '../../../../../api/constants';
import { handleImageUpload } from '../../../../../api/uploads';
import { apiErrorMessage, apiSucceeded } from '../apiUtils';

const ABOUT_MAX = 150;
const GENDERS = [
  { label: 'Male', value: 0 },
  { label: 'Female', value: 1 },
  { label: 'Other', value: 2 },
];

const todayMinus = (years) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0, 10);
};

const ProfileStep = ({ profile, setProfile, pendingToken, onSubmit, onBack }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const coverInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const previewUrlsRef = useRef({ cover: null, avatar: null });

  useEffect(() => () => {
    if (previewUrlsRef.current.cover) URL.revokeObjectURL(previewUrlsRef.current.cover);
    if (previewUrlsRef.current.avatar) URL.revokeObjectURL(previewUrlsRef.current.avatar);
  }, []);

  const authHeaders = pendingToken ? { 'x-auth-token': pendingToken } : {};

  const handleFile = (field, previewField) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (previewUrlsRef.current[previewField]) URL.revokeObjectURL(previewUrlsRef.current[previewField]);
    const url = URL.createObjectURL(file);
    previewUrlsRef.current[previewField] = url;
    setProfile({ ...profile, [field]: file, [`${field}Preview`]: url });
  };

  const passwordStrongEnough = profile.password.length >= 8;
  const passwordsMatch = profile.password && profile.password === profile.confirmPassword;

  const canContinue =
    profile.firstName.trim().length > 0 &&
    profile.lastName.trim().length > 0 &&
    profile.gender !== null &&
    !!profile.dob &&
    passwordStrongEnough &&
    passwordsMatch &&
    !saving;

  const errorMessage = useMemo(() => {
    if (!touched) return '';
    if (!passwordStrongEnough) return 'Password must be at least 8 characters.';
    if (!passwordsMatch) return 'Passwords do not match.';
    return '';
  }, [touched, passwordStrongEnough, passwordsMatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canContinue) return;

    setSaving(true);
    setApiError('');

    const profileRes = await apiHandler(
      PROFILE_UPDATE,
      'POST',
      {
        first_name: profile.firstName.trim(),
        last_name: profile.lastName.trim(),
        gender: profile.gender,
        dob: profile.dob,
        password: profile.password,
        ...(profile.aboutYou.trim() ? { bio: profile.aboutYou.trim() } : {}),
      },
      authHeaders,
    );

    if (!apiSucceeded(profileRes)) {
      setApiError(apiErrorMessage(profileRes, 'Failed to save profile.'));
      setSaving(false);
      return;
    }

    if (profile.avatarPhoto) {
      try {
        const photoUrl = await handleImageUpload(profile.avatarPhoto, 'profileImages/', 'image/jpeg', authHeaders);
        await apiHandler(PROFILE_PIC_UPDATE, 'POST', { photo: photoUrl }, authHeaders);
      } catch (_) { /* non-fatal: profile is already saved */ }
    }

    if (profile.coverPhoto) {
      try {
        const coverUrl = await handleImageUpload(profile.coverPhoto, 'coverImages/', 'image/jpeg', authHeaders);
        await apiHandler(PROFILE_COVER_PIC_UPDATE, 'POST', { cover_photo: coverUrl }, authHeaders);
      } catch (_) { /* non-fatal: profile is already saved */ }
    }

    setSaving(false);
    onSubmit();
  };

  return (
    <StepShell
      step={4}
      title="Set up your profile"
      subtitle="This is how the Nemonto community will know you"
      onBack={onBack}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
        <div
          className="ob-cover-upload"
          style={profile.coverPhotoPreview ? { backgroundImage: `url(${profile.coverPhotoPreview})` } : undefined}
          onClick={() => coverInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          {!profile.coverPhotoPreview && <>📷 <span>Add cover photo</span></>}
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFile('coverPhoto', 'cover')}
          />
        </div>

        <div className="ob-avatar-wrap">
          <div
            className="ob-avatar-circle"
            style={profile.avatarPhotoPreview ? { backgroundImage: `url(${profile.avatarPhotoPreview})` } : undefined}
            onClick={() => avatarInputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            {!profile.avatarPhotoPreview && '👤'}
            <span className="ob-avatar-edit">📷</span>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFile('avatarPhoto', 'avatar')}
            />
          </div>
        </div>

        <div className="ob-name-row">
          <div className="ob-field">
            <label className="ob-label">First name</label>
            <input
              type="text"
              className="ob-input"
              value={profile.firstName}
              onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
              placeholder="First name"
            />
          </div>
          <div className="ob-field">
            <label className="ob-label">Last name</label>
            <input
              type="text"
              className="ob-input"
              value={profile.lastName}
              onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
              placeholder="Last name"
            />
          </div>
        </div>

        <div className="ob-field">
          <label className="ob-label">Gender</label>
          <div className="ob-pill-row">
            {GENDERS.map((g) => (
              <div
                key={g.label}
                className={`ob-pill${profile.gender === g.value ? ' selected' : ''}`}
                onClick={() => setProfile({ ...profile, gender: g.value })}
                role="button"
                tabIndex={0}
              >
                {g.label}
              </div>
            ))}
          </div>
        </div>

        <div className="ob-field">
          <label className="ob-label">Date of birth</label>
          <div className="ob-hint">Your age won't be shown publicly</div>
          <input
            type="date"
            className={`ob-date-native${profile.dob ? ' filled' : ''}`}
            value={profile.dob}
            max={todayMinus(13)}
            min={todayMinus(100)}
            onChange={(e) => setProfile({ ...profile, dob: e.target.value })}
          />
        </div>

        <div className="ob-field">
          <label className="ob-label">About you</label>
          <textarea
            className="ob-textarea"
            maxLength={ABOUT_MAX}
            value={profile.aboutYou}
            onChange={(e) => setProfile({ ...profile, aboutYou: e.target.value })}
            placeholder="Tell people about your cooking style"
          />
          <div className="ob-charcount">{profile.aboutYou.length} / {ABOUT_MAX}</div>
        </div>

        <div className="ob-field">
          <label className="ob-label">Password</label>
          <div className="ob-password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              className="ob-input"
              value={profile.password}
              onChange={(e) => setProfile({ ...profile, password: e.target.value })}
              placeholder="Set a password"
            />
            <button
              type="button"
              className="ob-eye-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon off={showPassword} />
            </button>
          </div>
        </div>

        <div className="ob-field">
          <label className="ob-label">Confirm password</label>
          <div className="ob-password-field">
            <input
              type={showConfirm ? 'text' : 'password'}
              className="ob-input"
              value={profile.confirmPassword}
              onChange={(e) => setProfile({ ...profile, confirmPassword: e.target.value })}
              placeholder="Re-enter your password"
            />
            <button
              type="button"
              className="ob-eye-toggle"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              <EyeIcon off={showConfirm} />
            </button>
          </div>
        </div>

        {errorMessage && <p className="ob-error-text">{errorMessage}</p>}
        {apiError && <p className="ob-error-text">{apiError}</p>}

        <div className="ob-spacer" />

        <button type="submit" className="ob-btn-primary" disabled={!canContinue}>
          {saving ? 'Saving…' : 'Continue'}
        </button>
      </form>
    </StepShell>
  );
};

export default ProfileStep;
